<template>
  <div v-show="displayFrontend" :class="{questions__save: safeFreeze}" class="questions">
    <iframe
      height="1"
      src="https://10996528.fls.doubleclick.net/activityi;src=10996528;type=conve0;cat=dyfle002;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ord=1;num=1?"
      style="display:none" width="1"></iframe>

    <h1 class="questions__header">{{ $t("Projectteam") }}</h1>
    <p class="questions--fullwidth">{{ $t('Your Dyflexis consultant is {name}', {name: consultantName}) }}.</p>
    <p class="questions--fullwidth">
      {{
        $t("We would like to know who is part of the project team from your organization and who will be participating in the training. Enter below who is joining, including their Dyflexis permission and function and/or department. The Dyflexis permission roles are explained below. We recommend using at least two system administrators, so that they can replace each other.")
      }}</p>

    <table>
      <tr>
        <td class="questions__userroles">{{ $t("Superuser") }}:</td>
        <td>{{ $t("All rights including salary data and system settings") }}</td>
      </tr>
      <tr>
        <td class="questions__userroles">{{ $t("Administrator") }}:</td>
        <td>{{ $t("All rights, but no insight into salary data and system settings") }}</td>
      </tr>
      <tr>
        <td class="questions__userroles">{{ $t("Departmentmanager") }}:</td>
        <td>{{ $t("Employee management, scheduling, time registration and leave of absence") }}</td>
      </tr>
    </table>

    <form id="trainingMembers">
      <div v-for="(employee, index) in projectTeam.member" class="questions__teammember">
        <p v-if="index===0">{{ $t("Projectmanager") }}:</p>
        <p v-if="index===1">{{ $t("Additional teammembers") }}:</p>

        <div v-show="employee.firstName!=='deleted'">
          <b-form-radio-group
            id="salutation"
            v-model="employee.salutation"
            button-variant="outline-primary"
            buttons
            class="teammember__saluationinput"
            name="radio-btn-outline">
            <b-form-radio id="heer" value="heer">
              {{ $t("Mr.") }}
            </b-form-radio>
            <b-form-radio id="mevrouw" value="mevrouw">
              {{ $t("Ms.") }}
            </b-form-radio>
            <b-form-radio id="null" value="null">
              {{ $t("Other") }}
            </b-form-radio>
          </b-form-radio-group>

          <input
            id="teammember__name"
            v-model="employee.firstName"
            :disabled="readOnly || (index === 0 && employee.firstName !== null)"
            :placeholder="$t('Firstname')"
            class="form-control"
            required
            type="text"/>

          <input
            id="teammember__name"
            v-model="employee.lastName"
            :disabled="readOnly || index === 0"
            :placeholder="$t('Lastname')"
            class="form-control"
            required
            type="text"/>

          <input
            id="teammember__title"
            v-model="employee.title"
            :disabled="readOnly"
            :placeholder="$t('Function')"
            class="form-control"
            type="text"/>

          <input
            id="teammember__email"
            v-model="employee.email"
            :disabled="readOnly"
            :placeholder="$t('Email')"
            class="form-control"
            required
            type="email"/>

          <input
            id="teammember__phone"
            v-model="employee.phone"
            :disabled="readOnly"
            :placeholder="$t('Phonenumber')"
            class="form-control"
            required
            type="tel"/>

          <div class="teammember__contactlanguage">
            <b-form-select
              v-model="employee.language"
              :disabled="readOnly"
            >
              <option
                disabled
                value="null">
                {{ $t("Language") }}
              </option>
              <option value="NL">
                {{ $t("Dutch") }}
              </option>
              <option value="EN">
                {{ $t("English") }}
              </option>
              <option value="DE">
                {{ $t("German") }}
              </option>
              <option value="FR">
                {{ $t("French") }}
              </option>
              <option value="ES">
                {{ $t("Spanish") }}
              </option>
            </b-form-select>
          </div>

          <div class="teammember__contactlanguage teammember__marginleft">
            <b-form-select
              v-model="employee.userRole"
              :disabled="readOnly"
              required>
              <option
                disabled
                value="null">
                {{ $t("Dyflexis Permissions") }}
              </option>
              <option value="superuser">
                {{ $t("Superuser") }}
              </option>
              <option value="administrator">
                {{ $t("Administrator") }}
              </option>
              <option value="departmentmanager">
                {{ $t("Departmentmanager") }}
              </option>
              <option value="noDyflexisUser" style="color: gray;">
                {{ $t("Won't be Dyflexis user") }}
              </option>
            </b-form-select>
          </div>

          <b-form-checkbox
            v-model="employee.productUpdates"
            v-b-popover.hover.left="$t('Periodically we inform our administrators about the introduction of new features. We recommend that you include the administrators and managers in these updates.')"
            :disabled="readOnly"
            class="teammember__boolean"
            name="check-button teammember__contactSettings"
            switch>{{ $t("Product updates") }}
          </b-form-checkbox>

          <b-button
            v-if="index>0"
            class="teammember__delete"
            variant="danger"
            @click="deleteRow(index)">
            {{ $t("Remove teammember") }}
          </b-button>

          <p v-if="employee.errorMessage" class="questions__inputerrordescription questions--fullwidth">
            {{ employee.errorMessage }}</p>
        </div>
      </div>
    </form>

    <b-button
      class="teammember__addRow"
      variant="success"
      @click="addRow">
      <b>+</b> {{ $t("Add teammember") }}
    </b-button>

    <h2 class="questions__subheader">
      {{
        $t("Of course, we will do everything we can to protect your data. For example, we are ISO27001 certified for information security. However, when a data breach occurs, we will notify you as soon as possible as required by the AVG and agreed with you in our Processing Agreement. At which email address can we best reach your organization in this case?")
      }}
    </h2>
    <h2 class="questions__subheader">
      {{ $t("E-mail address data leak") }}
    </h2>
    <input
      v-model="projectTeam.dataleakEmail"
      class="form-control"
      required
      type="email"/>

    <h1 class="question--marginTop questions__header">{{ $t("Timeline") }}</h1>
    <h2 class="questions__subheader">{{ $t("Expected start date with Dyflexis") }}</h2>
    <input
      v-model="projectTeam.timeLineStartDate"
      class="form-control"
      required
      type="text"/>

    <h2 class="questions__subheader">{{ $t("Expected salary processing date based on Dyflexis") }}</h2>
    <input
      v-model="projectTeam.timeLineStartDatePayroll"
      class="form-control"
      required
      type="text"/>

    <div v-show="partnerStatus===''">
      <h1 class="question--marginTop questions__header">{{ $t("Training") }}</h1>
      <h2 class="questions__subheader">{{ $t("At which location do you want to follow the Dyflexis training?") }}</h2>
      <b-form-radio-group
        id="salutation"
        v-model="projectTeam.trainingLocation"
        :disabled="readOnly"
        button-variant="outline-primary"
        name="radio-btn-outline">
        <b-form-radio id="In-office" value="In-office">
          {{ $t("In our office in The Hague") }}
        </b-form-radio>
        <b-form-radio id="Online" value="Online">
          {{ $t("Online via Teams") }}
        </b-form-radio>
        <b-form-radio v-show="trainingOnLocation" id="At customer" value="At customer">
          {{ $t("At the customer's office") }}
        </b-form-radio>
      </b-form-radio-group>
    </div>

    <div v-show="showPayrollConnection">
      <h1 class="question--marginTop questions__header">{{ $t("Contactdetails for setup payroll connection") }}</h1>
      <p>{{
          $t("If you manage the payroll connection yourself, you can enter the contact details of the application manager. In other cases, your accountant manages the payroll link.")
        }}</p>

      <h2 class="questions__subheader">{{ $t("Name contact person payroll connection") }}</h2>
      <input
        v-model="projectTeam.payrollConnectionContactName"
        class="form-control"
        required
        type="text"/>

      <h2 class="questions__subheader">{{ $t("Email address contact person payroll connection") }}</h2>
      <input
        v-model="projectTeam.payrollConnectionContactEmail"
        class="form-control"
        required
        type="email"/>

      <h2 class="questions__subheader">{{ $t("Name of accountant office (if applicable)") }}</h2>
      <input
        v-model="projectTeam.payrollConnectionAccountantName"
        class="form-control"
        required
        type="text"/>
    </div>

    <h1 class="question--marginTop questions__header">{{ $t("Remarks") }}</h1>
    <h2 class="questions__subheader">
      {{ $t("Comments about this project") }}
    </h2>
    <b-form-textarea
      v-model="projectTeam.projectTeamRemarks"
      class="questions__textarea"
      rows="3"/>

    <input :value="$t('Save')" type="button" @click="handleSave"/>
    <p v-show="showSaveStatus" class="questions--savesuccess">{{ $t("Saved successfully!") }}</p>
  </div>
</template>

<script>
import SystemRequestService from "@/SystemRequestService";
import GlobalFunctions from "@/GlobalFunctions";

export default {
  data() {
    return {
      consultantName: null,
      displayFrontend: false,
      readOnly: false,
      safeFreeze: false,
      showPayrollConnection: false,
      showSaveStatus: false,
      trainingOnLocation: false,
      partnerStatus: '',
      projectTeam: {
        dataleakEmail: null,
        trainingLocation: null,
        timeLineStartDate: null,
        timeLineStartDatePayroll: null,
        payrollConnectionContactName: null,
        payrollConnectionContactEmail: null,
        payrollConnectionAccountantName: null,
        projectTeamRemarks: null,
        member: [{
          salutation: null,
          firstName: null,
          lastName: null,
          title: null,
          email: null,
          phone: null,
          language: null,
          userRole: null,
          productUpdates: true,
          errorMessage: null,
        }],
      },
    };
  },
  name: "Projectteam",
  async beforeMount() {
    const result = await SystemRequestService.getProject(this.$route.params.hashedId);

    if (!result) {
      alert(this.$t("Error while obtaining your details. Please try again later or contact your Dyflexis consultant."));
      return;

    }

    this.showPayrollConnection = result.payrollConnection;
    this.consultantName = result.consultantName;
    this.partnerStatus = result.partnerStatus;
    this.trainingOnLocation = result.trainingOnLocation;
    this.projectTeam.member = result.projectTeam;
    this.projectTeam.dataleakEmail = result.dataleakEmail;
    this.projectTeam.payrollConnectionAccountantName = result.payrollConnectionAccountantName;
    this.projectTeam.payrollConnectionContactEmail = result.payrollConnectionContactEmail;
    this.projectTeam.payrollConnectionContactName = result.payrollConnectionContactName;
    this.projectTeam.trainingLocation = result.trainingLocation;
    this.projectTeam.timeLineStartDate = result.timeLineStartDate;
    this.projectTeam.timeLineStartDatePayroll = result.timeLineStartDatePayroll;
    this.projectTeam.projectTeamRemarks = result.projectTeamRemarks;

    if (this.projectTeam.member[0].userRole == null) {
      this.projectTeam.member[0].userRole = 'superuser';
    }

    this.readOnly = result.isSigned;
    GlobalFunctions.setReadOnly(result.isSigned);
    localStorage.setItem('startingObject', JSON.stringify(this.projectTeam));
    this.displayFrontend = true;
  },
  mounted() {
    //Adform Tracking Code BEGIN
    window._adftrack = Array.isArray(window._adftrack) ? window._adftrack : (window._adftrack ? [window._adftrack] : []);
    window._adftrack.push({
      HttpHost: 'track.adform.net',
      pm: 2380282,
      divider: encodeURIComponent('|'),
      pagename: encodeURIComponent('Dyflexis - Opportunity-Won')
    });
    (function () {
      var s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://s2.adform.net/banners/scripts/st/trackpoint-async.js';
      var x = document.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    })();
    //Adform Tracking Code END

    //Start of LinkedIn insight tag
    const _linkedin_partner_id = "84881";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    (function (l) {
      if (!l) {
        window.lintrk = function (a, b) {
          window.lintrk.q.push([a, b])
        };
        window.lintrk.q = []
      }
      let s = document.getElementsByTagName("script")[0];
      let b = document.createElement("script");
      b.type = "text/javascript";
      b.async = true;
      b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
      s.parentNode.insertBefore(b, s);
    })(window.lintrk);
    //End of LinkedIn insight tag
  },
  beforeRouteLeave: function (to, from, next) {
    if (!GlobalFunctions.acceptPageLeave(this.projectTeam, this.$t("You have made changes without saving them. Press cancel to ignore these changes and leave the page."))) {
      this.handleSave(false);
    }
    next();
  },
  methods: {
    addRow() {
      this.projectTeam.member.push({
        salutation: '',
        firstName: '',
        lastName: '',
        title: '',
        email: '',
        phone: '',
        language: 'EN',
        userRole: 'superuser',
        productUpdates: true,
        errorMessage: '',
      });
    },
    deleteRow(index) {
      this.projectTeam.member[index].firstName = 'deleted';
    },
    async handleSave(saveInitialObject) {
      let abort = false;
      const firstnameRequired = this.$t("Firstname is a required field.");
      const lastnameRequired = this.$t("Last name is a required field.");
      const emailRequired = this.$t("E-mail is a required field.");
      const permissionRequired = this.$t("Dyflexis permissions is a required field.");

      this.projectTeam.member.forEach(
        function (teamMember) {
          teamMember.errorMessage = '';

          if (teamMember.firstName === 'deleted') {
            return;
          }

          if (!teamMember.firstName) {
            teamMember.errorMessage += firstnameRequired;
          }

          if (!teamMember.lastName) {
            teamMember.errorMessage += lastnameRequired;
          }

          if (!teamMember.email) {
            teamMember.errorMessage += emailRequired;
          }

          if (!teamMember.userRole) {
            teamMember.errorMessage += permissionRequired;
          }

          if (teamMember.errorMessage !== '') {
            abort = true;
          }
        }
      );
      this.$forceUpdate();

      if (abort) {
        alert(this.$t("Not all required fields have been filled in, scroll up for details."));
        return;
      }

      this.safeFreeze = true;
      const result = await SystemRequestService.updateProject(this.projectTeam, this.$route.params.hashedId);
      if (!result || result.status !== 'success') {
        alert(this.$t("Error while updating, please send a screenshot of the fields on this page to your Dyflexis consultant!"));
        return;
      }

      if (saveInitialObject !== false) {
        localStorage.setItem('startingObject', JSON.stringify(this.projectTeam));
      }

      location.reload();
    },
  },
};
</script>
